// SlideCraft's own stylesheet presets (TODO §7.4).
//
// The slide's title, subtitle and source line are *SlideCraft* chrome — Gridd has no such
// thing, and its factory sheet accordingly names no preset for them. Rather than hardcode
// their look (or push these names into Gridd's own defaults, where they don't belong), we
// inject four presets into whatever sheet is in play:
//
//   Title     Body + 6pt, non-bold
//   Subtitle  Body + 4pt, non-bold, italic
//   Source    the sheet's own Caption preset
//   Sticker   Body, bold — and nothing else touched (§23.0.2)
//
// They are *seeded*, not fixed: each is derived from the sheet's Body (or Caption) at
// injection time, so a sheet with a bigger Body gets a proportionally bigger Title. Once
// the user edits them in the stylesheet editor and saves, the sheet carries its own
// authored values and nothing is derived any more — injection only ever ADDS a preset the
// sheet is missing, it never overwrites one that's there.
//
// The whole feature lives on this side of the seam: the Gridd package is untouched, the
// editor panel simply gets a sheet that already contains the four rows and edits them
// like any other preset (it renders `sheet.presets` generically). The one thing we cannot
// do from here is name them: the editor labels a row with the i18n key
// `menu.style.presets.<name>`, and Gridd ships that string for `Title` but not for
// `Subtitle`/`Source`/`Sticker`, so those three rows show the raw key until Gridd adds them.

import { GRIDD_FACTORY_SHEET } from "@/lib/gridd-factory-sheet";
import type { GriddSheet, GriddStyleSheetData } from "@/types/document";

type GriddPreset = GriddSheet["presets"][number];

export const STUDIO_PRESET_NAMES = [
  "Title",
  "Subtitle",
  "Source",
  "Sticker",
] as const;
export type StudioPresetName = (typeof STUDIO_PRESET_NAMES)[number];

// Only reached by a sheet that names no usable preset at all; matches Gridd's own Body.
const FALLBACK_BODY: GriddPreset = {
  name: "Body",
  fontFamily: "Arial",
  fontColor: "text",
  fontColor2: "text2",
  fontWeight: "normal",
  fontStyle: "normal",
  fontSize: "12pt",
  fontUnderlined: false,
};

function findPreset(
  sheet: GriddSheet,
  name: string | undefined,
): GriddPreset | undefined {
  if (!name) return undefined;
  return sheet.presets?.find((p) => p.name === name);
}

// The sheet's running-text preset: Body by name, else whatever it calls its default.
function bodyPreset(sheet: GriddSheet): GriddPreset {
  return (
    findPreset(sheet, "Body") ??
    findPreset(sheet, sheet.defaults?.preset) ??
    sheet.presets?.[0] ??
    FALLBACK_BODY
  );
}

// Points as authored ("12pt" → 12). Sizes stay in points end to end (§1.4).
function pointsOf(preset: GriddPreset): number {
  const n = parseFloat(preset.fontSize ?? "");
  return Number.isFinite(n) && n > 0 ? n : 12;
}

// The seed for one studio preset, derived from the sheet it is being added to.
function derivePreset(sheet: GriddSheet, name: StudioPresetName): GriddPreset {
  const body = bodyPreset(sheet);
  const bodyPt = pointsOf(body);
  switch (name) {
    case "Title":
      return {
        ...body,
        name,
        fontSize: `${bodyPt + 6}pt`,
        fontWeight: "normal",
      };
    case "Subtitle":
      return {
        ...body,
        name,
        fontSize: `${bodyPt + 4}pt`,
        fontWeight: "normal",
        fontStyle: "italic",
      };
    // §23.0.2 The sticker was the sheet's *highlight* preset, which in Gridd's factory sheet
    // is Calisto MT 22pt — larger than the slide title and in a family nothing else on the
    // slide uses, so it read as a second title competing with the first. A status flag should
    // be the quietest typography on the slide and the loudest colour, so it seeds from Body
    // and changes exactly one thing: weight. Size, family, style and colour are the body's.
    case "Sticker":
      return { ...body, name, fontWeight: "bold" };
    case "Source": {
      // Caption is Gridd's own small print (Body − 4pt, italic). Take it wholesale when
      // the sheet has one; derive the same shape when it doesn't.
      const caption = findPreset(sheet, "Caption");
      return caption
        ? { ...caption, name }
        : {
            ...body,
            name,
            fontSize: `${bodyPt - 4}pt`,
            fontWeight: "normal",
            fontStyle: "italic",
          };
    }
  }
}

// Derivation is pure and the sheets it runs on are immutable (Redux state / an iframe
// payload), so cache by identity. This also keeps the *result* identity stable, which the
// stylesheet editor depends on: it re-pushes into its iframe whenever its `styleSheet`
// prop changes identity, and a fresh object every render would fight the user's edits.
const sheetCache = new WeakMap<GriddSheet, GriddSheet>();
const dataCache = new WeakMap<GriddStyleSheetData, GriddStyleSheetData>();

// One sheet, guaranteed to carry the four studio presets. Returns the sheet untouched
// when it already has them — an authored value always wins over a derived seed.
export function ensureStudioPresets(sheet: GriddSheet): GriddSheet {
  const cached = sheetCache.get(sheet);
  if (cached) return cached;

  const missing = STUDIO_PRESET_NAMES.filter((n) => !findPreset(sheet, n));
  const next = missing.length
    ? {
        ...sheet,
        presets: [
          ...(sheet.presets ?? []),
          ...missing.map((n) => derivePreset(sheet, n)),
        ],
      }
    : sheet;

  sheetCache.set(sheet, next);
  return next;
}

// The whole stylesheet payload as the stylesheet editor should see it: every sheet carries
// the studio presets, so switching sheets in the panel keeps all four rows editable.
//
// A deck that has never saved a sheet has none to inject into (`sheets: {}` — Gridd would
// fall back to its own factory sheet inside the iframe and our rows would never appear),
// so we seed the factory sheet here. That copy is what the editor then edits and saves
// back onto the deck.
export function withStudioPresets(
  data: GriddStyleSheetData,
): GriddStyleSheetData {
  const cached = dataCache.get(data);
  if (cached) return cached;

  const selectedSheet = data?.selectedSheet || "default";
  const source =
    data?.sheets && Object.keys(data.sheets).length > 0
      ? data.sheets
      : { [selectedSheet]: GRIDD_FACTORY_SHEET };

  const sheets: Record<string, GriddSheet> = {};
  for (const [key, sheet] of Object.entries(source)) {
    sheets[key] = ensureStudioPresets(sheet);
  }

  const next: GriddStyleSheetData = { selectedSheet, sheets };
  dataCache.set(data, next);
  return next;
}
